import { createContext, type JSX, type ReactNode, useContext, useMemo } from 'react';
import { type CloseMenu } from '@common/components/Menu/index';

interface MenuContextProviderProps {
  closeMenu: CloseMenu;
  children: ReactNode;
}

interface MenuContextType {
  closeMenu: CloseMenu;
}

const MenuContext = createContext<MenuContextType | undefined>(undefined);

function MenuContextProvider({
  closeMenu,
  children,
}: Readonly<MenuContextProviderProps>): JSX.Element {
  const currentContext = useContext(MenuContext); // Do we have a parent menu context?
  const closeParent = useMemo(() => currentContext ? currentContext.closeMenu : undefined, [
    currentContext,
  ]);

  const context = useMemo(
    (): MenuContextType => {
      return {
        closeMenu: (action?: () => void) => {
          closeMenu(action);
          closeParent?.();
        },
      };
    },
    [closeMenu, closeParent],
  );

  return (
    <MenuContext.Provider value={context}>
      {children}
    </MenuContext.Provider>
  );
}

const errorMessage = 'useCloseMenu has to be used within <MenuContextProvider>';

export const useCloseMenu = () => {
  const currentContext = useContext(MenuContext);
  if (!currentContext) {
    throw new Error(errorMessage);
  }
  return currentContext.closeMenu;
};

export default MenuContextProvider;
